/**
 * Safely fetches a URL and converts it to a File object with verified content type matching the extension.
 * Helps prevent text/html MIME type errors when attachments redirect to login or 404 pages.
 * 
 * @param {string} url - The URL of the attachment to fetch
 * @param {string|null} customName - Optional custom filename for the generated File object
 * @returns {Promise<File>} - A Promise resolving to a File object
 */
export const fetchAttachmentAsFile = async (url, customName = null) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download attachment (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const fileName = customName || url.split("/").pop() || "attachment";
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  const mimeMap = {
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'csv': 'text/csv',
    'txt': 'text/plain'
  };

  // If the extension matches a known type but the server returned HTML,
  // it is almost certainly a redirect/error page
  if (mimeMap[ext] && blob.type === "text/html") {
    throw new Error(`MIME mismatch: Expected ${mimeMap[ext]} for .${ext} but received text/html (likely server redirect or error)`);
  }

  return new File([blob], fileName, { type: mimeMap[ext] || blob.type });
};
